(() => {
  const initialize = (scope = document) => {
  const page = scope.matches?.('[data-cv1-page]') ? scope : scope.querySelector?.('[data-cv1-page]');
  if (!page || page.dataset.cv1Ready === 'true') return;
  page.dataset.cv1Ready = 'true';

  const desktopHoverMedia = window.matchMedia('(min-width: 761px) and (hover: hover) and (pointer: fine)');
  const makeAutoplaySafe = (video) => {
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
  };
  const hydratePoster = (video) => {
    if (!video?.dataset.poster) return false;
    video.poster = video.dataset.poster;
    delete video.dataset.poster;
    return true;
  };
  const hydrateVideo = (video) => {
    if (!video) return false;
    hydratePoster(video);
    let changed = false;
    video.querySelectorAll('source[data-src]').forEach((source) => {
      if (!source.dataset.src) return;
      source.src = source.dataset.src;
      delete source.dataset.src;
      changed = true;
    });
    if (changed) video.load();
    return changed;
  };
  const safePlay = (video) => {
    if (!video) return undefined;
    makeAutoplaySafe(video);
    hydrateVideo(video);
    const promise = video.play();
    if (promise && typeof promise.catch === 'function') promise.catch(() => undefined);
    return promise;
  };

  const prepareHoverVideos = () => {
    const cards = [...page.querySelectorAll('.cv1-video-card')];
    const visibleCards = new Set();
    const isDesktopHover = () => desktopHoverMedia.matches;
    const hasIntersectionObserver = 'IntersectionObserver' in window;
    const preview = (video) => {
      video.removeAttribute('autoplay');
      video.pause();
      if (video.readyState >= 1 && Number.isFinite(video.duration)) {
        video.currentTime = Math.min(0.12, video.duration / 8);
      }
    };
    const playVisibleTouchVideos = () => {
      if (isDesktopHover() || document.hidden) return;
      const targets = hasIntersectionObserver ? [...visibleCards] : cards;
      targets.forEach((card) => {
        const video = card.querySelector('video');
        if (!video) return;
        video.setAttribute('autoplay', '');
        safePlay(video);
      });
    };
    const syncMode = () => {
      cards.forEach((card) => {
        const video = card.querySelector('video');
        if (!video) return;
        if (isDesktopHover()) {
          if (!hasIntersectionObserver) hydratePoster(video);
          preview(video);
        }
        else video.setAttribute('autoplay', '');
      });
      playVisibleTouchVideos();
    };

    cards.forEach((card) => {
      const video = card.querySelector('video');
      if (!video) return;
      let engaged = false;
      makeAutoplaySafe(video);
      const initializePreview = () => {
        if (isDesktopHover() && !engaged && video.paused) preview(video);
      };
      if (video.readyState >= 1) initializePreview();
      else video.addEventListener('loadedmetadata', initializePreview, { once: true });
      card.addEventListener('pointerenter', () => {
        if (!isDesktopHover()) return;
        engaged = true;
        safePlay(video);
      });
      card.addEventListener('pointerleave', () => {
        if (!isDesktopHover()) return;
        engaged = false;
        preview(video);
      });
      card.addEventListener('focusin', () => {
        if (!isDesktopHover()) return;
        engaged = true;
        safePlay(video);
      });
      card.addEventListener('focusout', () => {
        if (!isDesktopHover()) return;
        engaged = false;
        preview(video);
      });
      card.addEventListener('click', () => {
        if (isDesktopHover() && video.paused) safePlay(video);
      });
    });

    if (hasIntersectionObserver) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector('video');
          if (!video) return;
          if (entry.isIntersecting) {
            visibleCards.add(entry.target);
            hydratePoster(video);
          }
          else visibleCards.delete(entry.target);
          if (isDesktopHover()) return;
          if (entry.isIntersecting) safePlay(video);
          else video.pause();
        });
      }, { rootMargin: '20% 0px', threshold: 0.01 });
      cards.forEach((card) => observer.observe(card));
    }

    window.addEventListener('pageshow', playVisibleTouchVideos);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        cards.forEach((card) => card.querySelector('video')?.pause());
        return;
      }
      playVisibleTouchVideos();
    });
    document.addEventListener('touchstart', playVisibleTouchVideos, { once: true, passive: true });
    if (typeof desktopHoverMedia.addEventListener === 'function') desktopHoverMedia.addEventListener('change', syncMode);
    else if (typeof desktopHoverMedia.addListener === 'function') desktopHoverMedia.addListener(syncMode);
    syncMode();
  };

  const prepareLazyVideos = () => {
    const videos = [...page.querySelectorAll('video[data-cv1-lazy-video]')];
    if (!videos.length) return;
    const visibleVideos = new Set();
    videos.forEach(makeAutoplaySafe);

    const playVisibleVideos = () => {
      if (document.hidden) return;
      visibleVideos.forEach((video) => safePlay(video));
    };

    if (!('IntersectionObserver' in window)) {
      videos.forEach((video) => {
        visibleVideos.add(video);
        safePlay(video);
      });
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) {
          visibleVideos.add(video);
          safePlay(video);
        } else {
          visibleVideos.delete(video);
          video.pause();
        }
      });
    }, { rootMargin: '240px 0px', threshold: 0.01 });

    videos.forEach((video) => observer.observe(video));
    window.addEventListener('pageshow', playVisibleVideos);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        visibleVideos.forEach((video) => video.pause());
        return;
      }
      playVisibleVideos();
    });
  };

  const prepareGalleryCarousel = () => {
    const gallery = page.querySelector('[data-cv1-gallery]');
    if (!gallery) return;
    const slides = [...gallery.querySelectorAll('[data-cv1-gallery-slide]')];
    const dots = [...gallery.querySelectorAll('[data-cv1-gallery-dot]')];
    const previous = gallery.querySelector('[data-cv1-gallery-prev]');
    const next = gallery.querySelector('[data-cv1-gallery-next]');
    const current = gallery.querySelector('[data-cv1-gallery-current]');
    const total = gallery.querySelector('[data-cv1-gallery-total]');
    if (!slides.length || !previous || !next || !current) return;
    let activeIndex = 0;
    if (total) total.textContent = String(slides.length).padStart(2, '0');
    slides.forEach((slide, index) => slide.setAttribute('aria-label', `${index + 1} of ${slides.length}`));

    const showSlide = (requestedIndex) => {
      activeIndex = (requestedIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
      dots.forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      current.textContent = String(activeIndex + 1).padStart(2, '0');
    };

    previous.addEventListener('click', () => showSlide(activeIndex - 1));
    next.addEventListener('click', () => showSlide(activeIndex + 1));
    dots.forEach((dot) => {
      dot.addEventListener('click', () => showSlide(Number(dot.getAttribute('data-cv1-gallery-dot')) || 0));
    });
    gallery.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      showSlide(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    });
    showSlide(0);
  };

  const prepareIdentityPicker = () => {
    const stageVideo = page.querySelector('.cv1-stage__badge video');
    const stageLabel = page.querySelector('.cv1-stage__label strong');
    if (!stageVideo || !stageLabel) return;
    page.querySelectorAll('.cv1-picker button').forEach((button) => {
      button.addEventListener('click', () => {
        page.querySelectorAll('.cv1-picker button').forEach((item) => {
          item.classList.toggle('is-active', item === button);
          item.setAttribute('aria-pressed', item === button ? 'true' : 'false');
        });
        const source = stageVideo.querySelector('source');
        if (source && button.dataset.video) {
          delete source.dataset.src;
          source.src = button.dataset.video;
          stageVideo.load();
          safePlay(stageVideo);
        }
        stageVideo.setAttribute('aria-label', `${button.dataset.name || 'Selected'} emblem preview`);
        stageLabel.textContent = button.dataset.name || '';
      });
    });
  };

  const prepareDriveTabs = () => {
    const scene = page.querySelector('.cv1-drive__scene');
    const video = scene?.querySelector('video');
    const source = video?.querySelector('source');
    const number = scene?.querySelector('.cv1-drive__number');
    const label = scene?.querySelector('.cv1-drive__caption small');
    const title = scene?.querySelector('.cv1-drive__caption h3');
    const copy = scene?.querySelector('.cv1-drive__caption p');
    if (!scene || !video || !source || !number || !label || !title || !copy) return;
    page.querySelectorAll('.cv1-drive__tabs button').forEach((button) => {
      button.addEventListener('click', () => {
        page.querySelectorAll('.cv1-drive__tabs button').forEach((item) => {
          item.classList.toggle('is-active', item === button);
          item.setAttribute('aria-selected', item === button ? 'true' : 'false');
        });
        delete source.dataset.src;
        source.src = button.dataset.video || '';
        video.poster = button.dataset.poster || '';
        video.load();
        safePlay(video);
        number.textContent = button.dataset.number || '';
        label.textContent = `${button.dataset.name || ''} / CUSTOM MEDIA`;
        title.textContent = button.dataset.title || '';
        copy.textContent = button.dataset.copy || '';
      });
    });
  };

  const preparePreorderVideo = () => {
    const wrapper = page.querySelector('[data-cv1-preorder-video]');
    const video = wrapper?.querySelector('video');
    const button = wrapper?.querySelector('[data-cv1-preorder-play]');
    if (!wrapper || !video || !button) return;

    const showButton = () => {
      button.hidden = false;
      wrapper.classList.remove('is-playing');
    };
    const hideButton = () => {
      button.hidden = true;
      wrapper.classList.add('is-playing');
    };
    const playFilm = () => {
      hydrateVideo(video);
      video.controls = true;
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') promise.catch(showButton);
    };

    if ('IntersectionObserver' in window) {
      const posterObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          hydratePoster(video);
          posterObserver.unobserve(wrapper);
        });
      }, { rootMargin: '600px 0px', threshold: 0.01 });
      posterObserver.observe(wrapper);
    } else {
      hydratePoster(video);
    }

    button.addEventListener('click', playFilm);
    video.addEventListener('play', hideButton);
    video.addEventListener('pause', () => {
      if (!video.ended) showButton();
    });
    video.addEventListener('ended', () => {
      video.controls = false;
      video.load();
      showButton();
    });
  };

  // CV1_ASSISTED_FITMENT_V1
  const prepareVehicleForms = () => {
    const forms = [...page.querySelectorAll('[data-cv1-vehicle-form]')];
    if (!forms.length) return;

    const sync = (source, selector) => {
      const value = source.value;
      forms.forEach((form) => {
        const target = form.querySelector(selector);
        if (target && target !== source) target.value = value;
      });
    };

    forms.forEach((form) => {
      form.querySelector('[data-cv1-model]')?.addEventListener('input', (event) => sync(event.currentTarget, '[data-cv1-model]'));
      form.querySelector('[data-cv1-year]')?.addEventListener('input', (event) => sync(event.currentTarget, '[data-cv1-year]'));
    });
  };

  // CV1 Fitment Modal
  const injectFitmentStyles = () => {
    if (document.getElementById('cv1-fitment-styles')) return;
    const style = document.createElement('style');
    style.id = 'cv1-fitment-styles';
    style.textContent = `
      .cv1-fitment-overlay {
        position: fixed; inset: 0; z-index: 9999;
        background: rgba(0,0,0,0.55);
        display: flex; align-items: center; justify-content: center;
        padding: 1rem;
        opacity: 0; transition: opacity 0.2s ease;
      }
      .cv1-fitment-overlay.is-visible { opacity: 1; }
      .cv1-fitment-modal {
        background: #fff; border-radius: 16px;
        padding: 2rem 1.75rem; max-width: 420px; width: 100%;
        transform: translateY(12px); transition: transform 0.2s ease;
        position: relative;
      }
      .cv1-fitment-overlay.is-visible .cv1-fitment-modal { transform: translateY(0); }
      .cv1-fitment-modal__icon { font-size: 2rem; text-align: center; margin-bottom: 0.75rem; }
      .cv1-fitment-modal__title {
        font-size: 1.05rem; font-weight: 600; text-align: center;
        margin-bottom: 0.5rem; color: #111;
      }
      .cv1-fitment-modal__body {
        font-size: 0.875rem; color: #555; text-align: center;
        line-height: 1.5; margin-bottom: 1.25rem;
      }
      .cv1-fitment-modal__checklist {
        background: #f8f8f6; border-radius: 10px;
        padding: 1rem 1.125rem; margin-bottom: 1.25rem;
      }
      .cv1-fitment-modal__check {
        display: flex; align-items: flex-start; gap: 10px;
        margin-bottom: 0.625rem; cursor: pointer;
      }
      .cv1-fitment-modal__check:last-child { margin-bottom: 0; }
      .cv1-fitment-modal__check input[type=checkbox] {
        width: 16px; height: 16px; flex-shrink: 0;
        margin-top: 2px; accent-color: #111; cursor: pointer;
      }
      .cv1-fitment-modal__check span {
        font-size: 0.8125rem; color: #444; line-height: 1.4;
      }
      .cv1-fitment-modal__btns { display: flex; flex-direction: column; gap: 0.625rem; }
      .cv1-fitment-modal__confirm {
        width: 100%; padding: 0.875rem;
        background: #111; color: #fff;
        border: none; border-radius: 99px;
        font-size: 0.9375rem; font-weight: 500;
        cursor: pointer; transition: background 0.15s;
        text-align: center;
      }
      .cv1-fitment-modal__confirm:disabled { background: #ccc; cursor: not-allowed; }
      .cv1-fitment-modal__confirm:not(:disabled):hover { background: #333; }
      .cv1-fitment-modal__cancel {
        width: 100%; padding: 0.75rem;
        background: transparent; color: #666;
        border: 0.5px solid #ddd; border-radius: 99px;
        font-size: 0.875rem; cursor: pointer;
        text-align: center; transition: border-color 0.15s;
        text-decoration: none; display: block;
      }
      .cv1-fitment-modal__cancel:hover { border-color: #999; color: #333; }
      .cv1-fitment-modal__no-btns { display: flex; flex-direction: column; gap: 0.625rem; }
      .cv1-fitment-modal__support {
        width: 100%; padding: 0.875rem;
        background: #111; color: #fff;
        border: none; border-radius: 99px;
        font-size: 0.9375rem; font-weight: 500;
        cursor: pointer; text-decoration: none;
        display: block; text-align: center;
      }
      .cv1-fitment-modal__anyway {
        width: 100%; padding: 0.75rem;
        background: transparent; color: #888;
        border: 0.5px solid #ddd; border-radius: 99px;
        font-size: 0.8125rem; cursor: pointer;
        text-align: center; transition: border-color 0.15s;
        box-sizing: border-box;
      }
      .cv1-fitment-modal__anyway:hover { border-color: #999; color: #555; }
      .cv1-fitment-modal__close {
        position: absolute; top: 1rem; right: 1rem;
        width: 28px; height: 28px; border-radius: 50%;
        background: #f0f0f0; border: none; cursor: pointer;
        font-size: 1rem; line-height: 1;
        display: flex; align-items: center; justify-content: center;
        color: #666; transition: background 0.15s;
      }
      .cv1-fitment-modal__close:hover { background: #e0e0e0; color: #111; }
    `;
    document.head.appendChild(style);
  };

  const showFitmentModal = (type) => new Promise((resolve) => {
    injectFitmentStyles();
    const overlay = document.createElement('div');
    overlay.className = 'cv1-fitment-overlay';

    if (type === 'check') {
      overlay.innerHTML = `
        <div class="cv1-fitment-modal">
          <button class="cv1-fitment-modal__close" data-cv1-close aria-label="Close">&times;</button>
          <div class="cv1-fitment-modal__icon">&#9888;&#65039;</div>
          <div class="cv1-fitment-modal__title">Please confirm before ordering</div>
          <div class="cv1-fitment-modal__body">Some vehicles with this model year may have a camera or special mount inside the rear badge. Please confirm yours before proceeding.</div>
          <div class="cv1-fitment-modal__checklist">
            <label class="cv1-fitment-modal__check">
              <input type="checkbox" data-cv1-check>
              <span>My rear badge does <strong>not</strong> have a built-in camera</span>
            </label>
            <label class="cv1-fitment-modal__check">
              <input type="checkbox" data-cv1-check>
              <span>My rear badge does <strong>not</strong> have a flip-up mechanism</span>
            </label>
            <label class="cv1-fitment-modal__check">
              <input type="checkbox" data-cv1-check>
              <span>My badge is a standard adhesive roundel (74mm or 82mm)</span>
            </label>
          </div>
          <div class="cv1-fitment-modal__btns">
            <button class="cv1-fitment-modal__confirm" disabled data-cv1-confirm>Confirm &amp; buy now</button>
            <a class="cv1-fitment-modal__cancel" href="https://www.carluex.store/pages/contact">Not sure &mdash; contact support</a>
          </div>
        </div>`;
      document.body.appendChild(overlay);
      requestAnimationFrame(() => overlay.classList.add('is-visible'));

      const checks = overlay.querySelectorAll('[data-cv1-check]');
      const confirmBtn = overlay.querySelector('[data-cv1-confirm]');
      checks.forEach((cb) => cb.addEventListener('change', () => {
        confirmBtn.disabled = ![...checks].every((c) => c.checked);
      }));
      confirmBtn.addEventListener('click', () => {
        overlay.classList.remove('is-visible');
        setTimeout(() => { overlay.remove(); resolve(true); }, 200);
      });
      overlay.querySelector('[data-cv1-close]').addEventListener('click', () => {
        overlay.classList.remove('is-visible');
        setTimeout(() => { overlay.remove(); resolve(false); }, 200);
      });
    } else if (type === 'camera') {
      overlay.innerHTML = `
        <div class="cv1-fitment-modal">
          <button class="cv1-fitment-modal__close" data-cv1-close aria-label="Close">&times;</button>
          <div class="cv1-fitment-modal__icon">&#10060;</div>
          <div class="cv1-fitment-modal__title">This vehicle may not be compatible</div>
          <div class="cv1-fitment-modal__body">Your rear badge appears to contain an integrated camera. Removing it <strong>may permanently disable your parking camera system</strong>. We strongly advise against proceeding.</div>
          <div class="cv1-fitment-modal__no-btns">
            <a class="cv1-fitment-modal__support" href="https://www.carluex.store/pages/contact">Contact support</a>
            <button class="cv1-fitment-modal__anyway" data-cv1-anyway>Order anyway &mdash; I understand removing the badge may disable my camera</button>
          </div>
        </div>`;
      document.body.appendChild(overlay);
      requestAnimationFrame(() => overlay.classList.add('is-visible'));

      overlay.querySelector('[data-cv1-close]').addEventListener('click', () => {
        overlay.classList.remove('is-visible');
        setTimeout(() => { overlay.remove(); resolve(false); }, 200);
      });
      overlay.querySelector('[data-cv1-anyway]').addEventListener('click', () => {
        overlay.classList.remove('is-visible');
        setTimeout(() => { overlay.remove(); resolve(true); }, 200);
      });
    } else {
      overlay.innerHTML = `
        <div class="cv1-fitment-modal">
          <button class="cv1-fitment-modal__close" data-cv1-close aria-label="Close">&times;</button>
          <div class="cv1-fitment-modal__icon">&#10060;</div>
          <div class="cv1-fitment-modal__title">This vehicle may not be compatible</div>
          <div class="cv1-fitment-modal__body">Your rear badge appears to have a separate mounting base. The CARLUEX FRENIX requires a cable entry hole behind the badge. Vehicles with a fixed base mount typically do not have this hole, which prevents installation.<br><br>If you're willing to drill two small holes, installation may still be possible &mdash; contact us first.</div>
          <div class="cv1-fitment-modal__no-btns">
            <a class="cv1-fitment-modal__support" href="https://www.carluex.store/pages/contact">Contact support</a>
            <button class="cv1-fitment-modal__anyway" data-cv1-anyway>Order anyway &mdash; I understand and accept the risk</button>
          </div>
        </div>`;
      document.body.appendChild(overlay);
      requestAnimationFrame(() => overlay.classList.add('is-visible'));

      overlay.querySelector('[data-cv1-close]').addEventListener('click', () => {
        overlay.classList.remove('is-visible');
        setTimeout(() => { overlay.remove(); resolve(false); }, 200);
      });
      overlay.querySelector('[data-cv1-anyway]').addEventListener('click', () => {
        overlay.classList.remove('is-visible');
        setTimeout(() => { overlay.remove(); resolve(true); }, 200);
      });
    }
  });

  const prepareCart = () => {
    let adding = false;
    page.querySelectorAll('[data-cv1-add]').forEach((button) => {
      button.addEventListener('click', async () => {
        if (adding) return;
        const form = button.closest('section')?.querySelector('[data-cv1-vehicle-form]')
          || page.querySelector('[data-cv1-vehicle-form]');
        const modelInput = form?.querySelector('[data-cv1-model]');
        const yearInput = form?.querySelector('[data-cv1-year]');
        const model = modelInput?.value.trim() || '';
        const year = yearInput?.value.trim() || '';
        if (!modelInput?.checkValidity()) {
          modelInput?.reportValidity();
          modelInput?.focus();
          return;
        }
        if (!yearInput?.checkValidity()) {
          yearInput?.reportValidity();
          yearInput?.focus();
          return;
        }
        adding = true;
        const original = button.textContent;
        button.textContent = 'Adding\u2026';
        try {
          let fitmentNote = '';
          let fitmentVerdict = 'ship';
          let fitmentReason = 'none';
          try {
            const fitmentRes = await fetch('https://bmwv1-fitment-check.hi-8c2.workers.dev', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ model, year })
            });
            const fitmentData = await fitmentRes.json();
            fitmentVerdict = fitmentData.verdict || 'ship';
            fitmentReason = fitmentData.do_not_ship_reason || 'none';
            const camLabel = fitmentData.camera === 'no' ? 'No' : fitmentData.camera === 'yes' ? 'Yes' : 'Possible';
            const mntLabel = fitmentData.special_mount === 'no' ? 'No' : fitmentData.special_mount === 'yes' ? 'Yes' : 'Unknown';
            fitmentNote = `BMW ${model} ${year} | Badge: ${fitmentData.size} | Camera: ${camLabel} | Mount: ${mntLabel} | ${fitmentVerdict} | ${fitmentData.note}`;
          } catch(e) {
            fitmentNote = `BMW ${model} ${year} | AI check failed - manual verify needed`;
            fitmentVerdict = 'check';
          }

          if (fitmentVerdict === 'do_not_ship') {
            adding = false;
            button.textContent = original;
            const modalType = fitmentReason === 'camera' ? 'camera' : 'mount';
            const confirmed = await showFitmentModal(modalType);
            if (!confirmed) return;
            fitmentNote += fitmentReason === 'camera'
              ? ' | Customer self-confirmed despite camera warning'
              : ' | Customer self-confirmed despite mount warning';
          } else if (fitmentVerdict === 'check') {
            adding = false;
            button.textContent = original;
            const confirmed = await showFitmentModal('check');
            if (!confirmed) return;
            fitmentNote += ' | Customer self-confirmed';
          }

          adding = true;
          button.textContent = 'Adding\u2026';

          const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: Number(page.dataset.variantId),
              quantity: 1,
              properties: { 'BMW Model': model, 'Model Year': year },
            }),
          });
          await fetch(`${window.Shopify.routes.root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: fitmentNote })
          });
          if (!response.ok) throw new Error('Unable to add product');
          window.location.assign(`${window.Shopify.routes.root}cart`);
        } catch {
          adding = false;
          button.textContent = original;
          window.location.assign(`${window.Shopify.routes.root}products/carluex-frenix-custom-led-badge-for-bmw`);
        }
      });
    });
  };

  const prepareReveals = () => {
    const items = page.querySelectorAll('.cv1-reveal');
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    page.classList.add('cv1-motion-ready');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach((item) => observer.observe(item));
  };

  prepareHoverVideos();
  prepareLazyVideos();
  prepareGalleryCarousel();
  prepareIdentityPicker();
  prepareDriveTabs();
  prepareVehicleForms();
  prepareCart();
  preparePreorderVideo();
  prepareReveals();
  };

  initialize(document);

  if (!window.__cv1ThemeEditorBound) {
    window.__cv1ThemeEditorBound = true;

    document.addEventListener('shopify:section:load', (event) => {
      initialize(event.target);
    });

    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target;
      if (!(block instanceof Element)) return;

      const page = block.closest('[data-cv1-page]');
      if (!page) return;

      const slide = block.closest('[data-cv1-gallery-slide]');
      if (slide) {
        const gallery = slide.closest('[data-cv1-gallery]');
        const slides = [...gallery.querySelectorAll('[data-cv1-gallery-slide]')];
        const index = slides.indexOf(slide);
        if (index >= 0) gallery.querySelector(`[data-cv1-gallery-dot="${index}"]`)?.click();
      }

      const faqItem = block.closest('.cv1-faq details');
      if (faqItem) faqItem.open = true;
    });
  }
})();